/**
 * Customer configuration for backend-admin.
 *
 * All customer values come from the central /customers/{id}.json files
 * at the repo root - single source of truth shared with mobile and web.
 *
 * The active customer is selected via REACT_APP_CUSTOMER_ID env variable.
 */

export interface AdminCustomerConfig {
  id: string;
  name: string;
  displayName: string;
  /** URL shown as background on the login page (or empty for a solid color) */
  loginBackgroundUrl?: string;
  theme: {
    primaryColor: string;
    secondaryColor: string;
    secondaryLight?: string;
    secondaryDark?: string;
  };
  defaultLanguage: string;
  supportedLanguages: string[];  // e.g. ['sv', 'en', 'de']
}

// ─── Load from central JSON ──────────────────────────────────────────────────

function loadCustomerJson(customerId: string): AdminCustomerConfig {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const data = require(`../../customers/${customerId}.json`);
  return {
    id: data.id,
    name: data.name,
    displayName: data.displayName,
    theme: {
      primaryColor: data.theme.primaryColor,
      secondaryColor: data.theme.secondaryColor,
      secondaryLight: data.theme.secondaryLight,
      secondaryDark: data.theme.secondaryDark,
    },
    defaultLanguage: data.languages.default,
    supportedLanguages: data.languages.supported,
  };
}

// ─── Registry & export ───────────────────────────────────────────────────────

const customerIds = ['sodraberget', 'level5', 'kullaberg'];
const customers: Record<string, AdminCustomerConfig> = {};
for (const id of customerIds) {
  customers[id] = loadCustomerJson(id);
}

const customerId = process.env.REACT_APP_CUSTOMER_ID || 'sodraberget';

export const customerConfig: AdminCustomerConfig =
  customers[customerId] ?? customers['sodraberget'];
